import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "./UserBadge";
import { 
  MessageSquare, Flame, Zap, Eye, ChevronRight, MessageCircle, 
  Sparkles, Pin, Trophy, Shield, Tag, Filter
} from "lucide-react";
import { toast } from "sonner";

export default function EsportsCommunityFeed() {
  const [filter, setFilter] = useState("all");
  const [reactions, setReactions] = useState({});

  const { data: topicsData, isLoading } = useQuery({
    queryKey: ["esportsFeedTopics"],
    queryFn: async () => (await api.get("/topics/recent?limit=12")).data,
    staleTime: 30000,
  });

  const topics = Array.isArray(topicsData)
    ? topicsData
    : Array.isArray(topicsData?.topics)
    ? topicsData.topics
    : Array.isArray(topicsData?.items)
    ? topicsData.items
    : [];

  const handleReaction = (topicId, type, e) => {
    e.preventDefault();
    e.stopPropagation();
    setReactions(prev => {
      const current = prev[`${topicId}_${type}`] || 0;
      return {
        ...prev,
        [`${topicId}_${type}`]: current + 1
      };
    });
    toast.success(`Ai adăugat o reacție ${type}!`);
  };

  const filteredTopics = topics.filter(t => {
    if (!t) return false;
    if (filter === "all") return true;
    if (filter === "hot") return (t.posts_count || t.replies_count || 0) > 3 || (t.views || 0) > 50;
    const bName = typeof t.board_name === "string" ? t.board_name.toLowerCase() : "";
    const title = typeof t.title === "string" ? t.title.toLowerCase() : "";
    if (filter === "cs2") return bName.includes("cs2") || title.includes("cs2");
    if (filter === "cs16") return bName.includes("1.6") || title.includes("1.6");
    if (filter === "tournaments") return bName.includes("turn") || title.includes("cup") || title.includes("turneu");
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Feed Controls Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#080c14] border border-[#161f30] p-4 rounded-xl shadow-lg">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[var(--tf-primary)]/20 border border-[var(--tf-primary)]/40 flex items-center justify-center text-[var(--tf-primary)]">
            <Zap className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-chakra font-black uppercase text-white tracking-wide flex items-center gap-2">
              <span>FEED COMUNITATE // ESPORTS STREAM</span>
              <span className="text-[10px] font-mono bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                LIVE
              </span>
            </h3>
            <p className="text-[11px] font-mono text-zinc-400">
              Discuții, mix-uri 5v5, anunțuri și meta Counter-Strike
            </p>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 bg-[#05070d] p-1 rounded-lg border border-[#141b2a] overflow-x-auto max-w-full">
          {[
            { id: "all", label: "Toate" },
            { id: "hot", label: "Hot 🔥" },
            { id: "cs2", label: "CS2" },
            { id: "cs16", label: "CS 1.6" },
            { id: "tournaments", label: "Turnee" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1 text-[11px] font-chakra font-bold uppercase tracking-wider rounded-md transition-all whitespace-nowrap ${
                filter === tab.id
                  ? "bg-[var(--tf-primary)] text-black shadow-sm"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Feed Cards Stream */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-[#080c14] border border-[#161f30] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filteredTopics.length === 0 ? (
        <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-8 text-center text-zinc-400 font-mono text-xs">
          Nu s-au găsit postări în acest filtru. Fii primul care începe o discuție!
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3.5">
          {filteredTopics.map(topic => {
            const author = topic.author || topic.user || { username: topic.author_name || "Operator", role: "member" };
            const fireCount = (reactions[`${topic.id}_fire`] || 0) + (topic.reaction_counts?.fire || topic.reaction_counts?.like || 2);
            const headshotCount = (reactions[`${topic.id}_hs`] || 0) + (topic.reaction_counts?.headshot || 1);
            const aceCount = (reactions[`${topic.id}_ace`] || 0) + (topic.reaction_counts?.ace || 0);

            return (
              <article
                key={topic.id}
                className="bg-[#080c14] border border-[#161f30] hover:border-[var(--tf-primary)]/50 rounded-xl p-4 sm:p-5 transition-all shadow-md group relative flex flex-col justify-between"
              >
                <div>
                  {/* Top Bar: Author, Badges, Category */}
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <Avatar user={author} size={42} />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Username user={author} className="text-sm font-bold font-chakra text-white" />
                          {author.role === "root" && (
                            <span className="px-1.5 py-0.2 text-[8px] font-black uppercase bg-amber-500 text-black font-chakra rounded">
                              ROOT
                            </span>
                          )}
                          {author.role === "admin" && (
                            <span className="px-1.5 py-0.2 text-[8px] font-black uppercase bg-red-600 text-white font-chakra rounded">
                              ADMIN
                            </span>
                          )}
                          {author.is_vip && (
                            <span className="px-1.5 py-0.2 text-[8px] font-black uppercase bg-amber-400/20 text-amber-300 border border-amber-400/30 font-chakra rounded">
                              VIP
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                          {topic.created_at ? new Date(topic.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Recent"}
                        </div>
                      </div>
                    </div>

                    {/* Board / Game Pill */}
                    {topic.board_name && (
                      <Link
                        to={`/forums/${topic.board_id}`}
                        className="px-2.5 py-1 rounded bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-[10px] font-mono text-zinc-300 uppercase tracking-wider transition-colors shrink-0"
                      >
                        #{topic.board_name}
                      </Link>
                    )}
                  </div>

                  {/* Title & Preview */}
                  <Link to={`/topic/${topic.id}`} className="block group-hover:text-[var(--tf-primary)] transition-colors">
                    <h4 className="text-base sm:text-lg font-chakra font-black uppercase text-zinc-100 group-hover:text-[var(--tf-primary)] leading-snug mb-1.5 flex items-center gap-2">
                      {topic.is_pinned && <Pin className="w-4 h-4 text-amber-400 shrink-0 rotate-45" />}
                      <span>{topic.title}</span>
                    </h4>
                    {topic.content && (
                      <p className="text-xs text-zinc-400 line-clamp-2 leading-relaxed font-sans mb-3">
                        {topic.content.replace(/<[^>]*>?/gm, '')}
                      </p>
                    )}
                  </Link>
                </div>

                {/* Footer Reactions & Metrics Strip */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#121824] mt-2">
                  {/* Reaction Buttons */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      onClick={(e) => handleReaction(topic.id, "fire", e)}
                      className="px-2.5 py-1 rounded-lg bg-[#05070d] hover:bg-orange-500/20 border border-white/10 hover:border-orange-500/50 text-[11px] font-mono text-zinc-300 hover:text-orange-400 flex items-center gap-1.5 transition-all"
                      title="Reacție Foc"
                    >
                      <span>🔥</span>
                      <span className="font-bold">{fireCount}</span>
                    </button>

                    <button
                      onClick={(e) => handleReaction(topic.id, "hs", e)}
                      className="px-2.5 py-1 rounded-lg bg-[#05070d] hover:bg-red-500/20 border border-white/10 hover:border-red-500/50 text-[11px] font-mono text-zinc-300 hover:text-red-400 flex items-center gap-1.5 transition-all"
                      title="Reacție Headshot"
                    >
                      <span>🎯</span>
                      <span className="font-bold">{headshotCount}</span>
                    </button>

                    <button
                      onClick={(e) => handleReaction(topic.id, "ace", e)}
                      className="px-2.5 py-1 rounded-lg bg-[#05070d] hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/50 text-[11px] font-mono text-zinc-300 hover:text-amber-400 flex items-center gap-1.5 transition-all"
                      title="Reacție Ace"
                    >
                      <span>⚡</span>
                      <span className="font-bold">{aceCount}</span>
                    </button>
                  </div>

                  {/* Views and Comments Metrics */}
                  <div className="flex items-center gap-3 text-xs font-mono text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5 text-zinc-400" />
                      <span>{topic.views || 12}</span>
                    </span>
                    <Link
                      to={`/topic/${topic.id}`}
                      className="flex items-center gap-1 text-cyan-400 hover:underline font-bold"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span>{topic.posts_count || topic.replies_count || 0} răspunsuri</span>
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* View Full Forum Button */}
      <div className="text-center pt-2">
        <Link
          to="/forums"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 text-white font-chakra font-black text-xs uppercase tracking-wider transition-all"
        >
          <span>EXPLOREAZĂ TOATE CATEGORIILE DE FORUM</span>
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
